import React, { useContext, useEffect, Suspense } from 'react'
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { AppContext } from './contexts/AppContexts';
import { Preloader } from '../pages/_components/Preloader';
import { VettingProvider } from './contexts/VettingContext';

const Home = React.lazy(() => import('../pages/home/page'));
const AboutUs = React.lazy(() => import('../pages/about/page'));

const SustainableDirectory = React.lazy(() => import('../pages/sustainability_directory/page'));
const SingleDirectoryPage = React.lazy(() => import('../pages/sustainability_directory/sections/SingleDirectoryPage'));

const Events = React.lazy(() => import('../pages/events/page'));
const SingleEvent = React.lazy(() => import('../pages/events/sections/SingleEvent'));
const FetchIndividualEvents = React.lazy(() => import('../pages/events/actions/ViewPersonalEvents'));
const PostEventPage = React.lazy(() => import('../pages/events/actions/PostEvent'));

const Resources = React.lazy(() => import('../pages/resources/index.jsx'));
const MoreResources = React.lazy(() => import('../pages/resources/sections/AllResourceView'));

const AuthenticationPage = React.lazy(() => import('../pages/authentication/page'));
const ContactPage = React.lazy(() => import('../pages/contact/page'));
const CommunityPage = React.lazy(() => import('../pages/community/page'));

const UserProfile = React.lazy(() => import('../pages/profile/page'));
const SingleResource = React.lazy(() => import('../pages/resources/SingleResource'));

const General = React.lazy(() => import('../pages/profile/general'));
const PostResource = React.lazy(() => import('../pages/resources/actions/PostResource'))
const FetchIndividualResources = React.lazy(() => import('../pages/resources/actions/ViewPersonalResources'))

const VettingPage = React.lazy(() => import('../pages/vetting/page'));
const VettingForm = React.lazy(() => import('../pages/vetting/actions/VettingForm'));
const Policies = React.lazy(() => import('../pages/policies/page'));
const TermsConditions = React.lazy(() => import('../pages/policies/terms_conditions'));

const ListingInfo = React.lazy(() => import('../pages/sustainability_directory/sections/AboutDirectory'));

const AdvisoryGroup = React.lazy(() => import('../pages/advisory_group/page'));
const NotFound = React.lazy(() => import('../pages/404'));
const CompletionistPage = React.lazy(() => import('../pages/vetting/sections/CompletionistPage'));

const GlobalAuth = React.lazy(() => import('../pages/authentication/GlobalAuth'));
const RemindMeLater = React.lazy(() => import('../pages/_components/remindMeLater'));
const SelfAssessmentReport = React.lazy(() => import('../pages/vetting/SelfAssessmentReport'));

const AboutChallenge = React.lazy(() => import('../pages/about/index.jsx'));
const Eligibility = React.lazy(() => import('../pages/eligibility/index.jsx'));
const ApplyNow = React.lazy(() => import('../pages/apply/index.jsx'));

const OAuthRedirect = React.lazy(() => import('../pages/authentication/sections/OAuthRedirect'));

const AdminRoutes = React.lazy(() => import('../pages/vetting/admin/routes'));
const PasswordResetConfirm = React.lazy(() => import('../pages/password-reset/PasswordResetConfirm'));

function NavigationRoutes() {
  const { userData, startAuth, setStartAuth } = useContext(AppContext)
  const location = useLocation();
  const navigate = useNavigate()

  useEffect(() => {
    // No form_filled check needed anymore
    // Commenting out redirect to prevent navigation issues with inner routes
    /*if (userData?.user_data) {
      navigate('/vetting');
    }*/
  }, [userData, navigate]);

  const AuthLauncher = () => {
    setStartAuth(true)
    navigate('/')
  }
  const HomeRedirect = () => {
    navigate('/')
  }

  return (
    <Suspense fallback={<Preloader />}>
      <Routes>
        <Route path="/" element={
          <Home />
        } />
        <Route path="/oauth2redirect" element={<OAuthRedirect />} />
        <Route path='/about' element={<AboutChallenge />} />
        <Route path='/eligibility' element={<Eligibility />} />
        <Route path='/apply' element={<ApplyNow />} />
        <Route path='/resources' element={<Resources />} />

        <Route path='/about/:section' element={<AboutUs />} />

        <Route path='/sustainable-directory' element={<SustainableDirectory />} />
        <Route path='/sustainable-directory/info' element={<ListingInfo />} />
        <Route path='/sustainable-directory/:listingMapper' element={<SingleDirectoryPage />} />

        <Route path="/resources/all" element={<MoreResources />} />
     

        <Route path='/events' element={<Events />} />
        <Route path='/events/:eventMapper' element={<SingleEvent />} />
        <Route path='/events/my-events' element={userData ? <FetchIndividualEvents /> : <HomeRedirect />} />
        <Route path='/events/post' element={userData ? <PostEventPage /> : <HomeRedirect />} />



        {/* <Route path='/events/{eventMapper}' element={<SingleEvent />} /> */}
        <Route path='/authentication' element={userData ? <AuthenticationPage /> : <AuthLauncher />} />
        <Route path='/contact' element={<ContactPage />} />
        <Route path='/community' element={<CommunityPage />} />
        {/* <Route path='/login' element={<Login />} /> */}
        <Route path='/profile' element={userData ? <UserProfile /> : <HomeRedirect />} />
        <Route path='/resource/:id' element={<SingleResource />} />

        <Route path='/general' element={userData ? <General /> : <HomeRedirect />} />
        <Route path='/resources/post' element={userData ? <PostResource /> : <HomeRedirect />} />
        <Route path='/resources/my-resources' element={userData ? <FetchIndividualResources /> : <HomeRedirect />} />

        <Route path='/privacy-policy' element={<Policies />} />
        <Route path='/terms-conditions' element={<TermsConditions />} />

        <Route path='/advisory-group' element={<AdvisoryGroup />} />

        <Route path='/vetting' element={
          userData ? (
            <VettingProvider>
              <VettingPage />
            </VettingProvider>
          ) : <HomeRedirect />
        } />

        {/* Admin Routes */}
        <Route path='/vetting/admin/*' element={
          userData && userData.user_data && userData.user_data.role === 'admin' ? (
            <VettingProvider>
              <AdminRoutes />
            </VettingProvider>
          ) : <HomeRedirect />
        } />

        <Route path='/vetting/form/:categoryId' element={
          userData ? (
            <VettingProvider>
              <VettingForm />
            </VettingProvider>
          ) : <HomeRedirect />
        } />

        <Route path='/vetting/completion' element={
          userData ? (
            <VettingProvider>
              <CompletionistPage />
            </VettingProvider>
          ) : <HomeRedirect />
        } />

        <Route path='/global-auth' element={<GlobalAuth />} />
        <Route path='/remind-me-later' element={<RemindMeLater />} />
        <Route path='/report' element={<SelfAssessmentReport />} />
        <Route path='/404' element={<NotFound />} />

        <Route path='/password-reset/confirm/:uidb64/:token' element={<PasswordResetConfirm />} />

        <Route path='*' element={<NotFound />} />
      </Routes>

    </Suspense>

  )
}

export default NavigationRoutes